import React, { FC } from 'react';
import pluralize from 'pluralize';
import {
  Box,
  CardContent,
  CardMedia,
  Tooltip,
  Typography,
} from '@mui/material';
import classNames from 'classnames';
import { Link } from 'react-router-dom';
import { IconCheck, IconProduct, IconTrash } from '../../../utils/icons';
import { IProduct } from '../../../components/Forms/Product/constants';
import { truncateStyle } from '../../admin/productsV2/productListView/constants';
import { useUser } from '../../../hooks/useUser';
import { isAM } from '../../../utils';
import { PdpCountPill, PlacedPill } from '../../../components/PdpCountPill/PdpCountPill';

interface Props {
  product: IProduct;
  totalStories?: number;
  totalVideos?: number;
  isSelected?: boolean;
  onSelect?: (isChecked: boolean) => void;
  onDelete?: () => void;
  brandId?: string;
}

export const SingleProduct: FC<Props> = ({
  product,
  totalVideos,
  totalStories,
  isSelected,
  onSelect,
  onDelete,
  brandId,
}) => {
  const { role } = useUser();
  const canDelete = isAM(role);

  return (
    <div
      key={product.desireid}
      className={classNames(
        'flex flex-col min-w-[264px] gap-3 p-4 rounded-lg border bg-surface border-border-token w-full',
        {
          'hover:border-accent cursor-pointer': Boolean(onSelect),
        }
      )}
      onClick={() => onSelect?.(!isSelected)}
    >
      <Box className="flex justify-between align-center gap-2">
        <Tooltip title={product.title} arrow placement="top">
          {brandId ? (
            <Link
              to={`/products/${brandId}?productId=${product.desireid}`}
              onClick={(e) => e.stopPropagation()}
              className="!text-md !font-bold text-text-1 hover:text-accent-text hover:underline cursor-pointer"
              style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '100%',
                marginTop: '3px',
                textDecoration: 'none',
              }}
            >
              {product.title}
            </Link>
          ) : (
            <Typography
              className="!text-md !font-bold text-text-1"
              sx={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '100%',
                marginTop: '3px',
              }}
            >
              {product.title}
            </Typography>
          )}
        </Tooltip>
        {canDelete && onDelete && (
          <div
            className="flex items-center justify-center cursor-pointer w-6 h-6 bg-surface hover:bg-surface-2 rounded-md text-muted"
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
          >
            {/* stroke via currentColor — SVG presentation attributes can't read var() */}
            <IconTrash width={16} height={16} stroke="currentColor" />
          </div>
        )}
        {isSelected && (
          <div className="flex items-center justify-center cursor-pointer w-6 h-6 bg-accent hover:bg-primaryDark rounded-xl">
            <IconCheck width={24} height={24} stroke="#fff" />
          </div>
        )}
      </Box>
      <Box className="relative">
        {product.image ? (
          <CardMedia
            component="img"
            style={{ height: 155, objectFit: 'contain' }}
            image={product.image}
            alt=""
            className="rounded-lg bg-tint"
          />
        ) : (
          <Box className="flex h-[155px] items-center justify-center bg-tint rounded-xl">
            <IconProduct />
          </Box>
        )}
        <Box className="absolute bottom-2 right-2 flex gap-3">
          {Boolean(totalVideos) && (
            <Typography
              className="bg-surface rounded-lg px-2 py-1 font-[CircularStd] !text-xs"
              color="text.secondary"
            >
              {pluralize('Video', totalVideos, true)}
            </Typography>
          )}
          {Boolean(totalStories) && (
            <Typography
              className="bg-surface rounded-lg px-2"
              color="text.secondary"
            >
              {pluralize('Story', totalStories, true)}
            </Typography>
          )}
          <PdpCountPill count={product.pdp_url_count} />
          <PlacedPill count={product.placed_count} />
        </Box>
      </Box>
      <CardContent className="!p-0 h-5">
        <Typography
          className="font-normal !text-sm"
          sx={truncateStyle(1)}
          color="text.secondary"
        >
          {product.short_description}
        </Typography>
      </CardContent>
    </div>
  );
};
